/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import { ExpansionPanelDetails, Hidden, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Stepper, { StepType } from 'components/Stepper';
import { CustomConnector } from 'components/Stepper/styles';
import { challengeSelector } from 'reducers/challenge.reducer';
import { getSafeHTML } from 'utils';
import ROUTES from 'constants/routes';
import DescriptionSkeleton from './DescriptionSkeleton';
import {
  StyledCard,
  StyledNote,
  StyledTitle,
  StyledPanel,
  StyledPanelSummary,
  StyledDetailsLink,
  StyledHistoryLink,
  StyledLastDeclaration,
  getCssCustomStepper,
  StyledTeaser,
} from './styles';

const getStepWithStatus =
  (currentStep: number) =>
    (step: StepType, index: number): StepType => {
      if (index === currentStep) {
        return { ...step, status: 'next' };
      }
      if (index < currentStep) {
        return { ...step, status: 'done' };
      }
      // when index > currentStep
      return { ...step, status: 'disabled' };
    };

type Props = {
  challengeId: number;
  currentStep: number;
  steps: StepType[];
};

const DeclarationDescription = ({
  challengeId,
  currentStep,
  steps,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { data } = useSelector(challengeSelector(challengeId));

  const safeTeaser = getSafeHTML(data.teaser || '');

  if (!data) {
    return <DescriptionSkeleton />;
  }

  const lastDeclaration = data?.currentChallengeMetaData?.lastDeclarationDate;

  const detailLink = (
    <StyledDetailsLink
      to={`${ROUTES.CHALLENGE}/${challengeId}`}
      color="inherit"
    >
      {t('challenges.declaration.seeDetail')}
    </StyledDetailsLink>
  );

  const historyLink = (
    <StyledHistoryLink
      to={`${ROUTES.CHALLENGE}${ROUTES.HISTORY}/${challengeId}`}
      color="inherit"
    >
      {t('challenges.declaration.see_history')}
      <ArrowRightAltIcon fontSize="large" />
    </StyledHistoryLink>
  );

  return (
    <React.Fragment>
      {!!lastDeclaration && (
        <StyledLastDeclaration>
          <p>
            {t('challenges.declaration.lastDeclaration', {
              date: new Date(lastDeclaration).toLocaleDateString(),
            })}
          </p>
          {historyLink}
        </StyledLastDeclaration>
      )}
      <StyledCard>
        <StyledTitle variant="h3">
          <strong>{data.title}</strong>
        </StyledTitle>

        {/* Mobile */}
        <Hidden mdUp>
          <StyledPanel>
            <StyledPanelSummary
              expandIcon={<ExpandMoreIcon color="primary" fontSize="large" />}
            >
              <Typography variant="caption">
                {t('challenges.declaration.readMore')}
              </Typography>
            </StyledPanelSummary>
            <ExpansionPanelDetails>
              <div
                dangerouslySetInnerHTML={{
                  __html: safeTeaser,
                }}
              />
              <div>{detailLink}</div>
            </ExpansionPanelDetails>
          </StyledPanel>
        </Hidden>

        {/* Desktop */}
        <Hidden smDown>
          <div>{detailLink}</div>
          <StyledTeaser
            dangerouslySetInnerHTML={{
              __html: safeTeaser,
            }}
          />
        </Hidden>

        <StyledNote paragraph>
          {t('challenges.declaration.declareToParticipate').toUpperCase()}
        </StyledNote>

        <Stepper
          steps={steps.map(getStepWithStatus(currentStep))}
          currentStep={currentStep}
          connector={<CustomConnector />}
          css={getCssCustomStepper(currentStep)}
        />
      </StyledCard>
    </React.Fragment>
  );
};

export default DeclarationDescription;
